import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import find from 'lodash/find';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';

import {
  deleteImage,
  getImages,
  getPresignedUrl,
  renameImage,
} from '../../../store/images/_images';
import useVerifyOrgRole from '../../../store/hooks/useVerifyOrgRole';

function ImageContainer(props) {
  const { resourceId, imageFilename } = props;
  const dispatch = useDispatch();

  const [resource, setResource] = useState({});

  const isOrgEditor = useVerifyOrgRole(resource?.org_id, 1);
  const storeUrls = useSelector((state) => state.images.presignedUrls);
  const organizations = useSelector((state) => state.organizations.data);
  const sites = useSelector((state) => state.sites.data);

  const [suffix, setSuffix] = useState('');
  const [ext, setExt] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (resourceId.startsWith('org')) {
      setResource(find(organizations, { org_id: resourceId }));
    } else if (resourceId.startsWith('site')) {
      setResource(find(sites, { site_id: resourceId }));
    }
  }, [resourceId, organizations, sites]);

  useEffect(() => {
    const lastDotIndex = imageFilename.lastIndexOf('.');
    const [name, ext] = [
      imageFilename.slice(0, lastDotIndex),
      imageFilename.slice(lastDotIndex),
    ];

    setSuffix(name);
    setExt(ext);
  }, [imageFilename]);

  useEffect(() => {
    let resourceUrls = get(storeUrls, `${resourceId}`);

    if (resourceUrls) {
      let _url = resourceUrls[`${suffix + ext}`];
      setUrl(_url);
    }
    // eslint-disable-next-line
  }, [storeUrls, resourceId]);

  const handleSuffixChange = (e) => {
    e.preventDefault();
    setSuffix(e.target.value);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await dispatch(
      renameImage({ resourceId, oldName: imageFilename, newName: suffix + ext })
    );
    dispatch(getImages(resourceId));
  };

  const handleViewImage = (e) => {
    e.preventDefault();
    dispatch(getPresignedUrl({ resourceId, key: suffix + ext }));
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    await dispatch(deleteImage({ resourceId, filename: imageFilename }));

    dispatch(getImages(resourceId));
  };

  return (
    <Card raised>
      <CardHeader
        titleTypographyProps={{ align: 'left' }}
        title={
          <TextField
            id='suffix'
            variant='standard'
            sx={{ width: '90%' }}
            onChange={handleSuffixChange}
            value={suffix}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>{ext}</InputAdornment>
              ),
            }}
          />
        }
        action={
          <Stack
            direction='row'
            spacing={2}
            justifyContent='flex-start'
            alignItems='center'
            mr={2}
            sx={{ width: '100%', mt: '2px' }}>
            <Button
              disabled={suffix + ext === imageFilename || !isOrgEditor}
              color='primary'
              variant='contained'
              size='small'
              onClick={handleSave}
              sx={{ height: '2rem' }}>
              Save
            </Button>
            <IconButton
              onClick={handleViewImage}
              sx={{ height: 33, width: 33 }}>
              <FontAwesomeIcon icon={['fal', 'image']} />
            </IconButton>
            <IconButton
              disabled={!isOrgEditor}
              onClick={handleDelete}
              sx={{ height: 33, width: 33 }}>
              <FontAwesomeIcon icon={['fal', 'trash']} />
            </IconButton>
          </Stack>
        }
      />
      {url ? (
        <CardMedia component='img' image={url} alt={suffix + ext} />
      ) : null}
    </Card>
  );
}

ImageContainer.propTypes = {
  resourceId: PropTypes.string,
  imageFilename: PropTypes.string.isRequired,
};

export default ImageContainer;
